import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Package, X, Bone, Cookie, Brush, Heart, Gift, Star } from 'lucide-react';

export default function BoxOpeningSection({ onSubscribeClick }) {
  const [isOpen, setIsOpen] = useState(false);
  const { t, isRomanian } = useLanguage();

  const boxItems = [
    { icon: Bone, title: t('box_item_1'), desc: t('box_item_1_desc'), color: 'bg-brand-orange' },
    { icon: Cookie, title: t('box_item_2'), desc: t('box_item_2_desc'), color: 'bg-brand-success' },
    { icon: Brush, title: t('box_item_3'), desc: t('box_item_3_desc'), color: 'bg-brand-brown' },
    { icon: Heart, title: t('box_item_4'), desc: t('box_item_4_desc'), color: 'bg-brand-orange' },
    { icon: Gift, title: t('box_item_5'), desc: t('box_item_5_desc'), color: 'bg-brand-accent' },
    { icon: Star, title: t('box_item_6'), desc: t('box_item_6_desc'), color: 'bg-brand-brown' },
  ];

  const closeText = isRomanian ? 'Închide cutia' : 'Close the box';
  const clickToOpenText = isRomanian ? 'Click pentru a deschide' : 'Click to open';

  return (
    <section id="whats-inside" className="py-10 md:py-14 bg-white relative overflow-hidden">
      <div className="section-container">
        <div className="text-center mb-8">
          <span className="sticker-badge mb-3">
            🐕 🐈 {t('box_badge')}
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-brand-brown mt-3 mb-3">
            {t('box_title')}
          </h2>
          <p className="text-brand-muted text-base">
            {t('box_subtitle')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Fixed height container to prevent layout shift */}
          <div className="relative">
            {/* Closed Box */}
            {!isOpen && (
              <div
                onClick={() => setIsOpen(true)}
                className="cursor-pointer transition-all duration-500 hover:scale-105 max-w-md mx-auto"
              >
                <div className="bg-brand-orange rounded-2xl p-6 md:p-8 text-center shadow-xl">
                  <Package className="w-14 h-14 text-white mx-auto mb-3" />
                  <p className="text-white font-heading text-lg font-bold mb-1">
                    {clickToOpenText}
                  </p>
                  <p className="text-white/80 text-xl">🐾</p>
                </div>
              </div>
            )}

            {/* Open Box Content */}
            {isOpen && (
              <div className="transition-all duration-500">
                <div className="bg-brand-cream rounded-3xl p-6 md:p-10 border-2 border-dashed border-brand-orange">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                    {boxItems.map((item, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-2xl p-4 md:p-6 text-center shadow-sm hover:shadow-lg transition-all hover:-translate-y-1"
                      >
                        <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                          <item.icon className={`w-6 h-6 ${item.color === 'bg-brand-accent' ? 'text-brand-brown' : 'text-white'}`} />
                        </div>
                        <h4 className="font-heading text-sm font-semibold text-brand-brown mb-1">
                          {item.title}
                        </h4>
                        <p className="text-xs text-brand-muted">{item.desc}</p>
                      </div>
                    ))}
                  </div>

                  {/* Close button - more prominent */}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="mt-8 mx-auto flex items-center gap-2 bg-brand-brown text-white px-6 py-3 rounded-full hover:bg-brand-brown-hover transition-colors font-medium"
                  >
                    <X className="w-5 h-5" />
                    <span>{closeText}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-4">
          <button
            onClick={onSubscribeClick}
            className="btn-primary text-base px-6 py-3"
            data-testid="box-cta"
          >
            {t('hero_cta')} →
          </button>
        </div>
      </div>
    </section>
  );
}
